import { ApolloClient, InMemoryCache, HttpLink, from, ApolloLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { generatePersistedQueryIdsFromManifest } from '@apollo/persisted-query-lists';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { fromPromise } from '@apollo/client/link/utils';
import { getAccessToken, getRefreshToken, clearAuthTokens, setAuthTokens } from '../utils/auth';
const API_URL = import.meta.env.VITE_API_URL;

let refreshPromise: Promise<boolean> | null = null;

const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_URL}/auth/refreshToken`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken, isHeimdall: true }),
    });

    if (!response.ok) throw new Error('Failed to refresh token');

    const data = await response.json();
    setAuthTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    return true;
  } catch (err) {
    console.error('[Token refresh error]:', err);
    return false;
  }
};

const authLink = new ApolloLink((operation, forward) => {
  const token = getAccessToken();
  operation.setContext({
    headers: {
      'x-motivo-client': 'admin',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (graphQLErrors?.some((error) => error.extensions?.code === 'PERSISTED_QUERY_HASH_NOT_FOUND')) {
    window.location.reload();
    return;
  }

  const isAuthError = graphQLErrors?.some((e) => e.message?.includes('Invalid JWT'));

  if (isAuthError) {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    return fromPromise(refreshPromise)
      .filter((success) => {
        if (!success) {
          clearAuthTokens();
          window.location.href = '/login';
        }
        return success;
      })
      .flatMap(() => {
        const token = getAccessToken();
        operation.setContext({
          headers: {
            ...operation.getContext().headers,
            Authorization: `Bearer ${token}`,
          },
        });
        return forward(operation);
      });
  }
});

const httpLink = new HttpLink({
  uri: `${API_URL}/graphql`,
});

const persistedQueryLink =
  import.meta.env.MODE === 'production'
    ? createPersistedQueryLink(
        generatePersistedQueryIdsFromManifest({
          // @ts-expect-error - placeholder JSON is overwritten by CI build
          loadManifest: () => import('../../persisted-query-manifest.json'),
        }),
      )
    : new ApolloLink((operation, forward) => forward(operation));

const client = new ApolloClient({
  link: from([persistedQueryLink, errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

export default client;
